/** @jsxImportSource @emotion/react */
import { useRef, useState } from "react";
import { OptionsOrGroups } from "react-select";
import AsyncSelect from "react-select/async";
import { LAYER_TYPE, Layer } from "../../actions/Layers/constants";
import { toast } from "../../utils/toast";
import apiClient from "../../constants/apiClient";
import { ROOT_URL } from "../../constants/urls";


import { useRouteLayer } from "../../hooks/useCurrentEntities";
import { formatOptionWithMeta } from "./metaOptionLabel";
import { normalizeMetaOptions } from "./metaOptions";

type MetaItem = {
    key: string;
    value: string;
};

type Option = {
    label: string;
    value: string;
    meta: MetaItem[];
};

export interface LayerState {
    layers: {
        current: Layer;
    };
}

type SingleLayerChildrenAutoCompleteProps = {
    type: LAYER_TYPE | null,
    setValue?: (field: string, value: string) => void,
    meta_options?: string[],
    field: string,
    post_value?: string,
};

type LayerChildrenAutoCompleteProps = {
    type?: LAYER_TYPE | null,
    setValue?: (field: string, value: string) => void,
    meta_options?: string[],
    field: string,
    post_value?: string,
};

export interface LayerChildrenAutoComplete {
    (props: SingleLayerChildrenAutoCompleteProps): JSX.Element,
    (props: LayerChildrenAutoCompleteProps): JSX.Element,
}

export const LayerChildrenAutoComplete: LayerChildrenAutoComplete = (({
    setValue, field, type, meta_options, post_value
}: LayerChildrenAutoCompleteProps) => {
    const timer = useRef<NodeJS.Timeout | null>(null);
    const layer = useRouteLayer();
    const [layer_meta, setLayerMeta] = useState([]);

    const searchLayers = (
        value: string,
        callback: (options: OptionsOrGroups<Option, never>) => void,
    ) => {
        if (timer.current !== null) {
            clearTimeout(timer.current);
        }

        timer.current = setTimeout(() => {
            apiClient
                .get(`${ROOT_URL}api/layers/search/`, {
                    params: {
                        text: value,
                        layer_type: type,
                        parent_layer_id: layer.id,
                    }
                })
                .then((response) => {
                    const metaKeys = normalizeMetaOptions(meta_options);
                    const options = response.data.map((i: any) => ({
                        value: i.label,
                        label: i.label,
                        meta: (metaKeys.length > 0
                            ? i.meta.filter((item: MetaItem) => metaKeys.some((key) => key === item.key))
                            : []
                        ).map((j: MetaItem) => ({ key: j.key, value: j.value })),
                    }));
                    callback(options);
                })
                .catch((error) => toast.error(`Could not fetch layers ${error}`));
        }, 350);
    };

    const _onLayerSelect = (values) => {
        const selectedValue = values ? values.value : "";
        if (setValue) {
            setValue(field, selectedValue);
        }
        setLayerMeta(values.meta);
    };

    const value = post_value ? { value: post_value, label: post_value, meta: layer_meta } : null;

    return <AsyncSelect
        formatOptionLabel={formatOptionWithMeta}
        loadOptions={searchLayers}
        onChange={_onLayerSelect}
        value={value}
    />;
}) as LayerChildrenAutoComplete;

export default LayerChildrenAutoComplete;
